/* eslint-disable no-plusplus */
import axios from 'axios';
import type { PromoCodeData } from 'common/interfaces/promoCode.interface';
import { showToastrMessage } from './Toastr.Utils';
import {
  getCartPricingReqPayload,
  getDesignGroupQuantity,
  resolveQuotedCartUnitPrice,
} from './PricingUtils';
import {
  DISABLED_CART_TOOLTIP_LICENSING,
  ADDED_TO_STORE_TOOLTIP,
  DISABLED_STORE_TOOLTIP_LICENSING,
  INPROGRESS_CART_TOOLTIP_LICENSING,
  INPROGRESS_STORE_TOOLTIP_LICENSING,
} from '@/constants/Order';
import { MostRecentActivityStatus } from '@/modules/MostRecent/MostRecentActivity';
import type {
  IModifiedParticipantsList,
  IModifiedSearchParticipantsList,
  IOrderColorPill,
  IOrderItem,
  IOrderItemForParticipants,
  IParticipant,
  IParticipantOrder,
  IPriceStepObj,
  IUpdatedPartOrderItem,
  GetQuoteMutationFunction,
  GetTimelineMutationFunction,
} from '@/common/interfaces/order.interface';
import type { S3SignedUrlResponse } from '@/api/S3Query';
import type {
  IAdditionalShippingPriceRes,
  IQuoteResponse,
} from '@/common/interfaces/quoter.interface';
import type {
  ICartItemDetail,
  ICartResponse,
  ISkuShippingTimeline,
  ITimelineElement,
} from '@/common/interfaces/cart.interface';
import { PROMO_CODE_TYPES } from '@/constants/Common';

/** Row from timeline API `shippingTimelines[]` — may include join keys when order ≠ cart order */
interface IShippingTimelineApiRow {
  shippingTimeline?: ITimelineElement[];
  skuShippingTimelines?: ISkuShippingTimeline[];
  proofItemId?: number;
  payloadObject?: { proofItemId?: number };
}

/** Payload fragment from `api/v4/shipping-timeline` used to build `timelineData` */
interface IShippingTimelineApiData {
  maxTimeline?: ITimelineElement[];
  shippingTimelines?: IShippingTimelineApiRow[];
}

/**
 * Build `ITimelineElement[]` from maxTimeline with optional date-range enrichment from shippingTimelines.
 */
export const extractAndEnrichTimeline = (data: IShippingTimelineApiData): ITimelineElement[] => {
  const normalizeShipType = (t: string | undefined): string => (t ?? '').toLowerCase();
  const { shippingTimelines } = data;

  const baseTimeline = (data.maxTimeline || []) as ITimelineElement[];
  const fallbackTimeline = (data.shippingTimelines?.[0]?.shippingTimeline ||
    []) as ITimelineElement[];
  let timelineData = baseTimeline.length > 0 ? baseTimeline : fallbackTimeline;

  if (shippingTimelines?.length) {
    timelineData = timelineData.map((entry) => {
      const hasValidRange =
        entry.newTimeline?.minDate?.dateInISOString && entry.newTimeline?.maxDate?.dateInISOString;
      if (hasValidRange) return entry;
      const fromShip = shippingTimelines
        .flatMap((s) => s.shippingTimeline ?? [])
        .find((t) => normalizeShipType(t.type) === normalizeShipType(entry.type));
      if (
        fromShip?.newTimeline?.minDate?.dateInISOString &&
        fromShip?.newTimeline?.maxDate?.dateInISOString
      ) {
        return { ...entry, newTimeline: fromShip.newTimeline };
      }
      return entry;
    });
  }

  return timelineData;
};

/**
 * Return color object with color name and hex code.
 *
 * @param items - Order Items
 */
export const getItemColors = (items: IOrderItem[]) => {
  const colorCodes: IOrderColorPill[] = [];

  items.forEach((item: IOrderItem) => {
    const {
      apparel: { hexCode, color },
    } = item;

    colorCodes.push({
      color: color || '',
      hex: hexCode?.toUpperCase() || '',
      orderItemId: item.orderItemId,
    });
  });

  return colorCodes;
};

/**
 * Method to get the design groups with unique style code.
 *
 * @param designgroup - Array of order Items.
 */
export const getDesignGroupswithUniqueStyleCode = (designgroup: IOrderItem[]) => {
  let styleCodeIds = designgroup.map((designItem) => designItem.apparel.styleCode);
  styleCodeIds = [...new Set(styleCodeIds)];

  const uniqueGroupsWithStyles = styleCodeIds.map((styleCode) =>
    designgroup.filter((designItem) => designItem.apparel.styleCode === styleCode)
  );

  return uniqueGroupsWithStyles;
};

/**
 * A method to filter out the design groups with unique style codes.
 *
 * @param items - Array of order items.
 * @param designGroupId - Design group ID.
 */
export const getUniqueStyleCodeDesigns = (items: IOrderItem[], designGroupId: number) => {
  const uniqueDesignGroups = items.filter((item) => item.groupOrderDesignGroupId === designGroupId);

  return getDesignGroupswithUniqueStyleCode(uniqueDesignGroups);
};

/** Find next available break point */
export const getNextBreakPoint = (
  breakpoints: Record<string, string>[],
  applicablePricePoint: number
) => {
  let nextIndex = 0;

  for (let i = 0; i < breakpoints.length; i++) {
    if (Object.keys(breakpoints[i])[0] === String(applicablePricePoint)) {
      nextIndex = i + 1;
    }
  }
  const nextBreakPoint = breakpoints[nextIndex]
    ? breakpoints[nextIndex]
    : breakpoints[nextIndex - 1];
  return nextBreakPoint;
};

/**
 * Method to calculate the order item breakpoinst and check whether the design group hit the minimum or not.
 *
 * @param totalOrderitems -  The order items.
 * @param specificDeisgnGroupOrderItems - Design group items to check.
 * @param designGroupId - the design group id
 * @param orderStatus - Order status.
 */
export const getPriceAndQuantityBarSteps = (
  totalOrderitems: IOrderItem[],
  specificDeisgnGroupOrderItems: IOrderItem[],
  designGroupId: number,
  orderStatus: string
) => {
  let pricePoints: IPriceStepObj[] = [];
  let minimumCard = false;
  let totalNumberOfUnits = 0;
  let totalUnits = 0;
  let productMessage = '';
  let isDesignGroupHitMinimum = false;

  const sameDesignGroups = totalOrderitems?.filter(
    (item) => item.groupOrderDesignGroupId === designGroupId
  );

  totalUnits = sameDesignGroups.reduce(
    (acc, productItem) => acc + (productItem.numUnits ? productItem.numUnits : 0),
    0
  );

  totalNumberOfUnits = totalUnits;

  const breakpoints = specificDeisgnGroupOrderItems[0]?.breakPoints?.split(';');

  const modifiedBreakpoints: Record<string, string>[] = breakpoints
    ? breakpoints.map((point) => ({ [point.split(':')[0]]: point.split(':')[1] }))
    : [];

  pricePoints = modifiedBreakpoints.map((point) => ({
    isExceeded: Number(Object.keys(point)[0]) < totalUnits,
    isTouched: Number(Object.keys(point)[0]) <= totalUnits,
    price: point[Object.keys(point)[0]],
    quantity: Object.keys(point)[0],
    showMinimumCard: false,
  }));

  minimumCard = pricePoints.length === 1;

  if (totalUnits) {
    /** Push the total units as breakpoint only when total units is not exactly equal to one of the break point */
    if (!pricePoints.find((point) => Number(point.quantity) === totalUnits)) {
      pricePoints.push({ quantity: String(totalUnits), isTotalUnits: true });
    }

    pricePoints = pricePoints.sort((a, b) => {
      const qtyA = Number(a.quantity);
      const qtyB = Number(b.quantity);

      if (qtyA > qtyB) {
        return 1;
      }
      if (qtyB > qtyA) {
        return -1;
      }
      return 0;
    });

    /** The index variable hold the index of the last hit Breakpoint/Minimum */
    const index = pricePoints.findIndex(
      (pricePoint) => !!(pricePoint.isTotalUnits || totalUnits === Number(pricePoint.quantity))
    );

    /** This is to make the quantity and Price value bold for last hit Breakpoint/Minimum */
    if (index > 0) {
      pricePoints[index - 1].lastHitPoint = true;
    } else if (totalUnits === Number(pricePoints[index].quantity)) {
      pricePoints[index].lastHitPoint = true;
    }
  }

  const breakpointsKeys = modifiedBreakpoints?.map((point) => Number(Object.keys(point)[0]));
  let applicablePricePoint = breakpointsKeys && breakpointsKeys[0];

  breakpointsKeys.forEach((key, i) => {
    if (i > 0) {
      if (breakpointsKeys[i - 1] <= totalUnits && totalUnits < key) {
        applicablePricePoint = breakpointsKeys[i - 1];
      }

      if (totalUnits >= key) {
        applicablePricePoint = key;
      }
    }
  });

  const breakpoint = modifiedBreakpoints.find(
    (bp) => Number(Object.keys(bp)[0]) === applicablePricePoint
  );

  if (breakpoint) {
    totalUnits = Number(breakpoint[applicablePricePoint]);
  }

  /** The minimum price breakpoint. */
  const mainApplicablePricePoint = Number(applicablePricePoint);

  if (totalNumberOfUnits < applicablePricePoint) {
    /** If any of the order item doesn't hit the minimum, it comes to IF block. */
    isDesignGroupHitMinimum = false;

    if (orderStatus === MostRecentActivityStatus.failedGroupOrder) {
      if (totalNumberOfUnits === 0) {
        productMessage = 'No orders were placed for this item.';
      } else {
        productMessage = `Such a bummer! You sold ${totalNumberOfUnits} but needed to sell ${applicablePricePoint}. We can help you try again.`;
      }
    } else if (totalNumberOfUnits === 0) {
      productMessage = `So far, you’ve sold ${totalNumberOfUnits}. Don’t forget to share to encourage everyone to buy before the store expires.`;
    } else {
      productMessage = `So far, you’ve sold ${totalNumberOfUnits}. Don’t forget to share to encourage everyone to buy before the store expires.`;
    }
  } else {
    isDesignGroupHitMinimum = true;
    const nextBreakpoint = getNextBreakPoint(modifiedBreakpoints, applicablePricePoint);
    const nextPoint = nextBreakpoint && Object.keys(nextBreakpoint)[0];
    const moreRequired =
      Number(nextPoint) - totalNumberOfUnits > 0 ? Number(nextPoint) - totalNumberOfUnits : 0;

    productMessage = moreRequired
      ? "Yay!! We'll start printing these as soon as we're done collecting orders. The price will continue to drop as more people buy, and we'll only charge everyone at the final price."
      : "Yay!! We'll start printing these as soon as we're done collecting orders.";
  }

  /** We must show a minimum card if the order item didn't meet the minimum but there was a quantity ordered. */
  const isItemHitMin = pricePoints.findIndex((point) => point.isTotalUnits);

  if (isItemHitMin === 0 && totalNumberOfUnits < mainApplicablePricePoint) {
    if (pricePoints[1]) {
      pricePoints[1].showMinimumCard = true;
    }
  } else if (isItemHitMin === -1 && totalNumberOfUnits < mainApplicablePricePoint) {
    if (pricePoints[0]) {
      pricePoints[0].showMinimumCard = true;
    }
  }

  return {
    pricePoints,
    isDesignGroupHitMinimum,
    productMessage,
    minimumCard,
    mainApplicablePricePoint,
    totalUnits: totalNumberOfUnits,
  };
};

const GROUP_ORDER_STORE_TIMEZONE = 'America/New_York';

/**
 * Returns the America/New_York calendar date as `YYYY-MM-DD`.
 *
 * @param value - Instant whose Eastern Time calendar day should be formatted.
 */
const getStoreCalendarDate = (value: Date): string => {
  const etDateParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: GROUP_ORDER_STORE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    etDateParts.find((entry) => entry.type === type)?.value ?? '';

  return `${part('year')}-${part('month')}-${part('day')}`;
};

/** True when both timestamps fall on the same America/New_York calendar day. */
export const isSameStoreCalendarDay = (
  date: string | number | Date | null | undefined,
  comparedTo: Date = new Date()
): boolean => {
  if (date == null || date === '') {
    return false;
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  return getStoreCalendarDate(parsed) === getStoreCalendarDate(comparedTo);
};

const ZERO_EXPIRATION_TIME = {
  orderDueDays: '00',
  orderDueHours: '00',
  orderDueMinutes: '00',
  orderDueSeconds: '00',
};

const padTimerUnit = (value: number): string => String(Math.max(0, value)).padStart(2, '0');

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const EXPLICIT_TIMEZONE_PATTERN = /(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

const getGroupOrderDueCalendarYmd = (groupOrderDate: string): string | null => {
  const trimmed = groupOrderDate.trim();
  if (!trimmed) {
    return null;
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    const utc = new Date(Date.UTC(year, month - 1, day));
    const isRealCalendarDate =
      utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day;

    return isRealCalendarDate ? trimmed : null;
  }

  if (!EXPLICIT_TIMEZONE_PATTERN.test(trimmed)) {
    return null;
  }

  const parsedMs = Date.parse(trimmed);
  if (Number.isNaN(parsedMs)) {
    return null;
  }

  // Same as v4-web: ISO timestamps contribute their UTC calendar date, then that YMD is Eastern.
  return new Date(parsedMs).toISOString().slice(0, 10);
};

/**
 * UTC millis for a wall-clock time in `timeZone` (handles EST/EDT).
 */
const zonedWallTimeToUtcMs = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string
): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  let utc = desiredAsUtc;

  for (let i = 0; i < 4; i += 1) {
    const parts = dtf.formatToParts(new Date(utc));
    const map: Record<string, string> = {};
    parts.forEach((part) => {
      if (part.type !== 'literal') {
        map[part.type] = part.value;
      }
    });
    const asUtc = Date.UTC(
      Number(map.year),
      Number(map.month) - 1,
      Number(map.day),
      Number(map.hour),
      Number(map.minute),
      Number(map.second)
    );
    utc += desiredAsUtc - asUtc;
  }

  return utc;
};

/**
 * Store close = Eastern midnight at the start of the day after `group_order_date`.
 * Matches v4-web `formatLiveStoreCountdownPill` so HQ, v4, and the storefront agree
 * regardless of the viewer's timezone.
 */
export const getGroupOrderStoreCloseTimestamp = (groupOrderDate: string): number | null => {
  const ymd = getGroupOrderDueCalendarYmd(groupOrderDate);
  if (!ymd) {
    return null;
  }

  const [year, month, day] = ymd.split('-').map(Number);
  const nextUtc = new Date(Date.UTC(year, month - 1, day));
  nextUtc.setUTCDate(nextUtc.getUTCDate() + 1);

  return zonedWallTimeToUtcMs(
    nextUtc.getUTCFullYear(),
    nextUtc.getUTCMonth() + 1,
    nextUtc.getUTCDate(),
    0,
    0,
    0,
    GROUP_ORDER_STORE_TIMEZONE
  );
};

/**
 * Remaining time until Eastern midnight after the due date. Same close instant as
 * v4-web and the storefront countdown.
 *
 * @param date - The group order close date.
 */
export const getOrderExpirationTime = (date: string) => {
  const closeMs = getGroupOrderStoreCloseTimestamp(date);
  if (closeMs == null) {
    return ZERO_EXPIRATION_TIME;
  }

  let seconds = Math.floor((closeMs - Date.now()) / 1000);
  if (seconds < 0) {
    seconds = 0;
  }

  let minutes = Math.floor(seconds / 60);
  let hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  hours -= days * 24;
  minutes = minutes - days * 24 * 60 - hours * 60;
  seconds = seconds - days * 24 * 60 * 60 - hours * 60 * 60 - minutes * 60;

  return {
    orderDueDays: padTimerUnit(days),
    orderDueHours: padTimerUnit(hours),
    orderDueMinutes: padTimerUnit(minutes),
    orderDueSeconds: padTimerUnit(seconds),
  };
};

/**
 * A dated group order is no longer live after Eastern midnight on the day after
 * group_order_date (same instant as the HQ/v4/storefront countdown).
 */
export const isGroupOrderStorePastCloseDate = (
  groupOrderDate: string | null | undefined
): boolean => {
  if (!groupOrderDate) {
    return false;
  }

  const closeMs = getGroupOrderStoreCloseTimestamp(groupOrderDate);
  if (closeMs == null) {
    return false;
  }

  return Date.now() > closeMs;
};

export const isLiveGroupOrderStoreForParticipantEdit = (
  orderStatus: string,
  groupOrderDate: string | null | undefined
): boolean => {
  if (orderStatus !== MostRecentActivityStatus.orderLive) {
    return false;
  }

  return !isGroupOrderStorePastCloseDate(groupOrderDate);
};

/** True when a permanent store has a close timestamp from the order API. */
export const hasPermanentStoreClosedAt = (closedAt?: string | null): boolean =>
  Boolean(closedAt && String(closedAt).trim());

/**
 * Method to return the order total.
 *
 * @param numUnits - Number of units.
 * @param breakPoints - Breakpoints of the GO.
 */
export const getOrderTotal = (numUnits: number, breakPoints: string): number => {
  let qty: string | null = null;
  let price: string | null = null;

  breakPoints.split(';').some((breakPoint: string, idx: number) => {
    const [breakpointQty, breakpointPrice] = breakPoint.split(':');
    qty = breakpointQty;

    if (numUnits >= Number(qty) || idx === 0) {
      price = breakpointPrice;
    } else {
      return true;
    }
    return false;
  });

  return parseFloat(price || '0');
};

const PARTICIPANT_ORDER_PLACED_TIMEZONE = 'America/New_York';

const getCalendarDatePartsInTimezone = (
  value: Date,
  timeZone: string
): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(value);

  return {
    year: Number(parts.find((part) => part.type === 'year')?.value ?? 0),
    month: Number(parts.find((part) => part.type === 'month')?.value ?? 0),
    day: Number(parts.find((part) => part.type === 'day')?.value ?? 0),
  };
};

const getCalendarDayDiffInTimezone = (value: Date, timeZone: string): number => {
  const target = getCalendarDatePartsInTimezone(value, timeZone);
  const today = getCalendarDatePartsInTimezone(new Date(), timeZone);
  const targetUtc = Date.UTC(target.year, target.month - 1, target.day);
  const todayUtc = Date.UTC(today.year, today.month - 1, today.day);

  return Math.round((todayUtc - targetUtc) / (24 * 60 * 60 * 1000));
};

/** Calendar days between an order-placed timestamp and today (same timezone as display labels). */
export const getParticipantOrderPlacedAgeInDays = (isoDateString: string): number => {
  const date = new Date(isoDateString);

  if (Number.isNaN(date.getTime())) {
    return 0;
  }

  return Math.max(0, getCalendarDayDiffInTimezone(date, PARTICIPANT_ORDER_PLACED_TIMEZONE));
};

const formatParticipantOrderPlacedTime = (date: Date, timeZone: string): string =>
  date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  });

/** Resolve order-placed timestamp from API participant payload. */
export const resolveParticipantOrderPlacedAt = (participant: IParticipant): string | undefined =>
  participant.order_placed_at ?? participant.orderPlacedAt ?? participant.created_at;

/** Display label for when a participant placed their order. */
export const formatParticipantOrderPlacedAt = (isoDateString?: string): string => {
  if (!isoDateString) {
    return '';
  }

  const date = new Date(isoDateString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const timePart = formatParticipantOrderPlacedTime(date, PARTICIPANT_ORDER_PLACED_TIMEZONE);
  const dayDiff = getCalendarDayDiffInTimezone(date, PARTICIPANT_ORDER_PLACED_TIMEZONE);

  if (dayDiff === 0) {
    return `Today · ${timePart}`;
  }

  if (dayDiff === 1) {
    return `Yesterday · ${timePart}`;
  }

  if (dayDiff === 2) {
    return `2 days ago · ${timePart}`;
  }

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: PARTICIPANT_ORDER_PLACED_TIMEZONE,
  });

  return `${datePart} · ${timePart}`;
};

/** Compact label: `Today · 6:41 AM`, `Yesterday · 6:41 AM`, or `May 17 · 6:41 AM`. */
export const formatParticipantOrderPlacedAtPill = (isoDateString?: string): string => {
  if (!isoDateString) {
    return '';
  }

  const date = new Date(isoDateString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const timePart = formatParticipantOrderPlacedTime(date, PARTICIPANT_ORDER_PLACED_TIMEZONE);
  const dayDiff = getCalendarDayDiffInTimezone(date, PARTICIPANT_ORDER_PLACED_TIMEZONE);

  if (dayDiff === 0) {
    return `Today · ${timePart}`;
  }

  if (dayDiff === 1) {
    return `Yesterday · ${timePart}`;
  }

  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: PARTICIPANT_ORDER_PLACED_TIMEZONE,
  });

  return `${datePart} · ${timePart}`;
};

/** Relative label for the most recent participant order on permanent store item cards. */
export const formatPermanentStoreLastOrderLabel = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const dayDiff = getCalendarDayDiffInTimezone(date, PARTICIPANT_ORDER_PLACED_TIMEZONE);

  if (dayDiff === 0) {
    return 'Last order today';
  }

  if (dayDiff === 1) {
    return 'Last order yesterday';
  }

  return `Last order ${dayDiff} days ago`;
};

/**
 * Method to return the group order participants list
 *
 * @param participants - The list of participants.
 * @param orderItems - The list of order items.
 */
export const getOrderParticipantData = (participants: IParticipant[], orderItems: IOrderItem[]) => {
  let participantsList: IModifiedParticipantsList[] = [];

  participants.forEach((participant) => {
    const participantObj: IModifiedParticipantsList = {
      ...participant,
      orderPlacedAt: resolveParticipantOrderPlacedAt(participant),
      expand: false,
      expandedPartOrderItems: [],
      participantOrderItems: [],
    };

    const participantOrderItems: IUpdatedPartOrderItem[] = [];

    participantObj.participant_order_items.forEach((pItem) => {
      /** I am writing this condition because when an order tips, we are going to show only the tipped items. */
      const orderItemInfo = orderItems.filter((item) => item.id === pItem.order_item_id)[0];

      if (orderItemInfo) {
        const pItemObj = { ...pItem, orderedSize: '', orderItemPrice: 0, design: {} };
        const { sizes, order_item: orderItem } = pItem;

        pItemObj.orderedSize = sizes ? sizes.split(':')[1] : '';
        pItemObj.orderItemPrice = orderItem.group_order_breakpoints
          ? getOrderTotal(orderItem.num_units, orderItem.group_order_breakpoints)
          : 0;

        pItemObj.design = orderItemInfo ? orderItemInfo.design : {};

        participantOrderItems.push(pItemObj);
      }
    });

    participantObj.participantOrderItems = participantOrderItems;
    participantObj.expandedPartOrderItems = participantOrderItems.slice(1);

    participantsList.push(participantObj);
  });

  participantsList = participantsList.filter((item) => item.participantOrderItems.length);

  return participantsList;
};

/**
 * Method to get the order item object after updating it with the price.
 *
 * @param items - Order item objects.
 */
export const getOrderItemObject = (items: IOrderItemForParticipants[]) =>
  items.map((item: IOrderItemForParticipants) => {
    const nItem = { ...item };
    const goItemPrice = item.group_order_breakpoints
      ? getOrderTotal(item.numUnits, item.group_order_breakpoints)
      : 0;
    nItem.orderItemPrice = item.price ? Number(item.price) : Number(goItemPrice);

    return nItem;
  });

/**
 * Method to get the participant order items after updating it with addresss, status, link and items.
 *
 * @param participantList - Participant order items.
 */
export const getParticipantOrderItemList = (participantList: IParticipantOrder[]) => {
  const list: IModifiedSearchParticipantsList[] = [];

  participantList.forEach((participant: IParticipantOrder, index: number) => {
    const participantObj: IModifiedSearchParticipantsList = {
      id: 0,
      name: '',
      expand: false,
      expandedPartOrderItems: [],
      participantOrderItems: [],
      address: '',
      status: '',
      trackingLink: '',
      trackingNumber: '',
    };

    participantObj.id = index + 1;
    participantObj.name = participant.name;

    participantObj.address = `${participant.street1}${
      participant.street2 ? `, ${participant.street2}` : ''
    }, ${participant.city}, ${participant.state}, ${participant.zip}`;

    if (participant.shipmentStatus) {
      participantObj.status =
        participant.shipmentStatus === 'DELIVERED' ? 'Delivered' : 'Out for Delivery';
    } else {
      participantObj.status = '';
    }

    participantObj.trackingLink = participant.trackingLink;
    participantObj.trackingNumber = participant.trackingNumber;

    participantObj.participantOrderItems = getOrderItemObject([participant.orderItems[0]]);
    participantObj.expandedPartOrderItems = getOrderItemObject(participant.orderItems.slice(1));

    list.push(participantObj);
  });

  return list;
};

/**
 * Method to retrieve the order item price.
 *
 * @param orderItem - The order item.
 */
export const getOrderItemPrice = (orderItem: IOrderItem) =>
  orderItem.breakPoints ? getOrderTotal(orderItem.numUnits, orderItem.breakPoints) : 0;

/**
 * Method to validate the tax exempt file.
 *
 * @param event - React input change event.
 */
export const handleTaxExemptFileValidations = (event: React.ChangeEvent<HTMLInputElement>) => {
  if (event.target.files?.length) {
    const selectedFile = event.target.files[0];

    /**
     * File type validation - application/pdf.
     */
    if (selectedFile.type !== 'application/pdf') {
      showToastrMessage(`Please select a PDF file.`, 'warning');
      return false;
    }

    /**
     * File size validation - max size - 25mb.
     */
    const maxSizeInBytes = 25 * 1024 * 1024; // 25MB
    if (selectedFile.size > maxSizeInBytes) {
      showToastrMessage(`File size exceeds 25MB limit.`, 'warning');
      return false;
    }

    return true;
  }
  return false;
};

/**
 * Function to upload the tax exempt certificate to s3
 * and return the uploaded filename to send in order payload.
 *
 * @param signedUrl - S3 signed url to upload the file.
 * @param file - Tax exempt pdf file to be uploaded.
 */
export const uploadTaxExemptFileToS3 = async (signedUrlData: S3SignedUrlResponse, file: File) => {
  try {
    /**
     * On fetched pre-signed url upload the file to s3.
     */
    if (signedUrlData.signedRequest) {
      const formData2 = new FormData();
      formData2.append('file', file);
      const options = {
        headers: {
          'x-amz-acl': 'private',
          'Content-Type': file.type,
          'Sec-Fetch-Dest': 'empty',
          'Sec-Fetch-Mode': 'cors',
          'Sec-Fetch-Site': 'cross-site',
        },
      };

      /**
       * Upload the file to S3.
       */
      const uploadedData = await axios.put(signedUrlData.signedRequest, formData2, options);

      if (uploadedData.status === 200) {
        return signedUrlData.fileName;
      }
    }

    return '';
  } catch (err) {
    showToastrMessage('Failed to upload the Tax Exempt Certificate.', 'failure');
    return '';
  }
};

/**
 * maxTimeline (+ shippingTimelines date enrichment) for arbitrary proof item ids — e.g. design group (all colors).
 */
export const getMaxTimelineDataForProofItems = async (
  getTimeline: GetTimelineMutationFunction,
  proofItemIds: number[]
): Promise<ITimelineElement[]> => {
  if (proofItemIds.length === 0) return [];

  const timelinePayload = {
    proofItemData: proofItemIds.map((proofItemId) => ({ proofItemId })),
    fetchMaxTimeline: true,
  };

  let timelineData: ITimelineElement[] = [];
  try {
    const timelineResponse = await getTimeline({ data: timelinePayload });

    if (timelineResponse && 'data' in timelineResponse) {
      const data = timelineResponse.data as IShippingTimelineApiData;
      timelineData = extractAndEnrichTimeline(data);
    }
  } catch (err) {
    console.error('TimelineQuery API error:', err);
    timelineData = [];
  }

  return timelineData;
};

/** Store cart drawer tiers are user-selected; quoter MOQ is only the product floor. */
export const resolveUpdatedCartItemMinQuant = (item: ICartItemDetail, quoteMoq: number): number => {
  const savedApiMinimum = Number(item.savedMinimumQuantity ?? 0);

  if (item.isStore && savedApiMinimum > 0) {
    return savedApiMinimum;
  }

  if (item.isStore) {
    const selectedMinimum = Number(item.minQuant ?? item.quantity ?? 0);

    if (selectedMinimum > 0) {
      return selectedMinimum;
    }
  }

  return quoteMoq;
};

/**
 * Function used in regular order component to update the cart items
 * with moq and additional shipping object array
 *
 * @param cartItems - fetched cartitems
 * @param getQuote - getQuote api mutation function
 */
export const getUpdatedCartItemsRegOrder = async (
  cartItems: ICartResponse,
  getQuote: GetQuoteMutationFunction,
  getTimeline: GetTimelineMutationFunction
) => {
  const quoterAddiPriceObj: { [key: string]: IAdditionalShippingPriceRes } = {};
  const itemShipPriceObject: { [key: string]: IAdditionalShippingPriceRes[] } = {};

  // Prepare timeline data payload
  const timelinePayload = {
    proofItemData: cartItems.map((itmDetail: ICartItemDetail) => ({
      proofItemId: itmDetail.proofItemId,
    })),
    fetchMaxTimeline: true,
  };

  let timelineData: ITimelineElement[] = [];
  let skuTimelinesByProofId: Map<number, ISkuShippingTimeline[]> | undefined;
  let perItemSkuTimelines: ISkuShippingTimeline[][] = [];
  let canUsePositionalIndex = false;
  try {
    const timelineResponse = await getTimeline({ data: timelinePayload });

    if (timelineResponse && 'data' in timelineResponse) {
      const data = timelineResponse.data as IShippingTimelineApiData;

      timelineData = extractAndEnrichTimeline(data);

      const { shippingTimelines } = data;
      const rows = shippingTimelines ?? [];
      perItemSkuTimelines = rows.map((item) => item.skuShippingTimelines ?? []);

      const rowsWithProofId = rows.filter(
        (r) => (r.proofItemId ?? r.payloadObject?.proofItemId) != null
      );
      if (rowsWithProofId.length > 0) {
        const timelinesByProofId = new Map<number, ISkuShippingTimeline[]>();
        rowsWithProofId.forEach((r) => {
          const id = r.proofItemId ?? r.payloadObject?.proofItemId;
          if (id != null) {
            timelinesByProofId.set(id, r.skuShippingTimelines ?? []);
          }
        });
        skuTimelinesByProofId = timelinesByProofId;
      } else {
        canUsePositionalIndex = rows.length === cartItems.length;
      }
    }
  } catch (err) {
    console.error('TimelineQuery API error:', err);
    timelineData = []; // Ensure it's an array even on error
  }

  const resolveSkuShippingTimelinesForItem = (
    itmDetail: ICartItemDetail,
    index: number
  ): ISkuShippingTimeline[] => {
    if (skuTimelinesByProofId) {
      return skuTimelinesByProofId.get(itmDetail.proofItemId) ?? [];
    }
    if (canUsePositionalIndex) {
      return perItemSkuTimelines[index] ?? [];
    }
    return [];
  };

  const updatedCartItems = await Promise.all(
    cartItems.map(async (itmDetail: ICartItemDetail, index: number) => {
      const designGroupQuantity = getDesignGroupQuantity(itmDetail, cartItems);
      const payload = getCartPricingReqPayload(itmDetail, designGroupQuantity);

      payload.apparels[0].additionalShipping.type = itmDetail.shipType;

      try {
        const response = await getQuote({ data: payload });

        if (response && 'data' in response) {
          const quoteBreakdown: IQuoteResponse = response.data[0];
          itemShipPriceObject[itmDetail.id] = quoteBreakdown.additionalShippingPriceObject;

          /**
           * This sets max of all additional shipping price object
           * we need in shipping component to set the est delivery date
           */
          quoteBreakdown.additionalShippingPriceObject.forEach(
            (itm: IAdditionalShippingPriceRes) => {
              if (quoterAddiPriceObj[itm.type]) {
                if (quoterAddiPriceObj[itm.type].daysToBeAdded < itm.daysToBeAdded) {
                  quoterAddiPriceObj[itm.type] = itm;
                }
              } else {
                quoterAddiPriceObj[itm.type] = itm;
              }
            }
          );

          const lineQuantity = Number(itmDetail.quantity) || 0;
          const quotedUnitPrice =
            lineQuantity > 0 ? resolveQuotedCartUnitPrice(itmDetail, quoteBreakdown) : null;
          const rawStrikedUnitPrice = Number(quoteBreakdown.finalPricePerUnitBeforeDiscount);
          const quotedStrikedUnitPrice =
            lineQuantity > 0 && itmDetail.discount && Number.isFinite(rawStrikedUnitPrice)
              ? rawStrikedUnitPrice
              : undefined;

          return {
            ...itmDetail,
            ...(quotedUnitPrice != null ? { price: quotedUnitPrice } : {}),
            ...(quotedStrikedUnitPrice != null
              ? { strikedPricePerUnit: quotedStrikedUnitPrice }
              : {}),
            ...(quoteBreakdown.breakpoints?.length
              ? { priceList: quoteBreakdown.breakpoints }
              : {}),
            minQuant: resolveUpdatedCartItemMinQuant(itmDetail, quoteBreakdown.moq),
            additionalShippingPriceObject: quoteBreakdown.additionalShippingPriceObject,
            timelineData,
            skuShippingTimelines: resolveSkuShippingTimelinesForItem(itmDetail, index),
          };
        }
        return {
          ...itmDetail,
          timelineData,
          skuShippingTimelines: resolveSkuShippingTimelinesForItem(itmDetail, index),
        };
      } catch (err) {
        console.error(err);
        return {
          ...itmDetail,
          timelineData,
          skuShippingTimelines: resolveSkuShippingTimelinesForItem(itmDetail, index),
        };
      }
    })
  );

  /** Timeline dates and eligibility come only from the shipping-timeline API (maxTimeline + enrichment).
   *  Quoter additionalShippingPriceObject is still used for pricing (quoterAddiPriceObj / per-line rows). */

  return {
    updatedCartItems,
    quoterAddiPriceObj,
    itemShipPriceObject,
  };
};
/**
 * Function to get the tooltip for design card.
 * @param isDisabled - design card is disabled or not
 * @param isDesignInProgress - design card is in progress or not
 * @param isStore - store or cart
 * @returns
 */
export const getDesignCardToolTip = (
  isDisabled: boolean,
  isDesignInProgress: boolean,
  isStore = false,
  isAlreadyInStore = false
) => {
  if (isAlreadyInStore && isStore) {
    return ADDED_TO_STORE_TOOLTIP;
  }

  if (isDesignInProgress) {
    return isStore ? INPROGRESS_STORE_TOOLTIP_LICENSING : INPROGRESS_CART_TOOLTIP_LICENSING;
  }

  if (isDisabled) {
    return isStore ? DISABLED_STORE_TOOLTIP_LICENSING : DISABLED_CART_TOOLTIP_LICENSING;
  }

  return isStore ? 'Add to Store' : 'Add to Cart';
};

/**
 * Function to get promo code discount.
 * @param subtotal - Subtotal of order
 * @param value - promo code value
 */
export const getPromoDiscount = (
  promoCode: PromoCodeData,
  subtotal: number,
  guarantee: string | undefined = undefined
) => {
  if (promoCode.type === PROMO_CODE_TYPES.percentage) {
    const absoluteDisc = +((subtotal * promoCode.value) / 100);

    if (subtotal <= +absoluteDisc) {
      return Math.abs(+subtotal).toFixed(2);
    }

    return Math.abs(+absoluteDisc).toFixed(2);
  }

  if (promoCode.type === PROMO_CODE_TYPES.noGuaranteeFee) {
    return guarantee === '100' ? String(promoCode.discount ?? '0') : '0';
  }

  if (subtotal <= +promoCode.value) {
    return Math.abs(+subtotal).toFixed(2);
  }
  return Math.abs(+promoCode.value).toFixed(2);
};

/**
 * Promo discount for HQ UI and checkout totals.
 * Uses v3-computed `discount` (restriction-aware) when present; only type 7
 * (no guarantee fee) is recomputed client-side from guarantee + subtotal.
 */
export const resolvePromoDiscount = (
  promoCode: PromoCodeData,
  orderSubtotal: number,
  guarantee?: string
): string => {
  if (promoCode.type === PROMO_CODE_TYPES.noGuaranteeFee) {
    return getPromoDiscount(promoCode, orderSubtotal, guarantee);
  }
  const rawDiscount = promoCode.discount;
  const fromServer =
    rawDiscount === null || rawDiscount === undefined ? '' : String(rawDiscount).trim();
  if (fromServer !== '') {
    return fromServer;
  }
  return getPromoDiscount(promoCode, orderSubtotal, guarantee);
};

/**
 * Type 7 (no guarantee fee) waives the guarantee line only; tax on merchandise
 * should use salesTaxAmount from the API, not discountedSalesTaxAmount.
 */
export const shouldUseDiscountedSalesTaxForPromo = (
  promoCode: PromoCodeData | undefined,
  resolvedDiscountNumeric: number
): boolean =>
  !!promoCode && resolvedDiscountNumeric > 0 && promoCode.type !== PROMO_CODE_TYPES.noGuaranteeFee;
